import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KanbanSquare, Loader2 } from 'lucide-react'

import {
  createProductionAssignments,
  getProductionBudget,
  type ProductionAssignmentItem,
  type ProductionBudgetView,
  type ProductionDepartment,
  type ProductionMember,
} from '@/api/studio-production'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { memberDisplayName } from '@/features/studio/modals/InviteMemberModal'
import {
  ProtoBadge,
  ProtoBtn,
  ProtoInput,
  ProtoLabel,
  ProtoModal,
  ProtoSelect,
} from '@/features/studio/components/ui-kit'

export interface AssignWorkItemPreset {
  departmentId?: string
  assigneeId?: number
  shotPublicId?: string
  shotSceneKey?: string
  bibleRefType?: string
  bibleRefPublicId?: string
  expectedKind?: string
  title?: string
  items?: Array<{ item_type: string; item_public_id: string; label?: string }>
}

function mapBibleRefToItemType(bibleRefType: string): string {
  switch (bibleRefType) {
    case 'character':
      return 'bible_character'
    case 'location':
      return 'bible_location'
    case 'prop':
      return 'bible_prop'
    case 'costume':
      return 'bible_costume'
    case 'style':
      return 'bible_style'
    default:
      return bibleRefType.startsWith('bible_') ? bibleRefType : `bible_${bibleRefType}`
  }
}

function presetToItems(preset?: AssignWorkItemPreset): Array<{ item_type: string; item_public_id: string; label?: string }> {
  if (!preset) return []
  if (preset.items?.length) return preset.items
  const items: Array<{ item_type: string; item_public_id: string; label?: string }> = []
  if (preset.shotPublicId) {
    items.push({ item_type: 'shot', item_public_id: preset.shotPublicId, label: preset.title })
  }
  if (preset.shotSceneKey) {
    items.push({ item_type: 'shot_scene', item_public_id: preset.shotSceneKey, label: preset.title })
  }
  if (preset.bibleRefType && preset.bibleRefPublicId) {
    items.push({
      item_type: mapBibleRefToItemType(preset.bibleRefType),
      item_public_id: preset.bibleRefPublicId,
      label: preset.title,
    })
  }
  return items
}

export function AssignWorkItemModal({
  open,
  onClose,
  productionId,
  members,
  departments,
  preset,
  gate,
  onAssigned,
}: {
  open: boolean
  onClose: () => void
  productionId: string
  members: ProductionMember[]
  departments: ProductionDepartment[]
  preset?: AssignWorkItemPreset
  gate: { isOwner: boolean; isDeptAdmin: boolean; myDepartmentId: string | null }
  onAssigned?: () => void
}) {
  const { t } = useTranslation()
  const [departmentId, setDepartmentId] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [quota, setQuota] = useState('500')
  const [budget, setBudget] = useState<ProductionBudgetView | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const scopeItems = useMemo(() => presetToItems(preset), [preset])

  useEffect(() => {
    if (!open) return
    setDepartmentId(preset?.departmentId ?? '')
    setAssigneeId(preset?.assigneeId ? String(preset.assigneeId) : '')
    setQuota('500')
    setError(null)
    void getProductionBudget(productionId)
      .then(setBudget)
      .catch(() => setBudget(null))
  }, [open, preset, productionId])

  const assigneeOptions = useMemo(() => {
    let pool = members
    if (departmentId) {
      pool = members.filter((m) => m.department_id === departmentId)
    } else if (!gate.isOwner && gate.isDeptAdmin && gate.myDepartmentId) {
      pool = members.filter((m) => m.department_id === gate.myDepartmentId)
    }
    return pool.map((m) => ({ value: String(m.user_id), label: memberDisplayName(m) }))
  }, [members, departmentId, gate])

  const availableHint = useMemo(() => {
    if (!budget) return null
    if (!gate.isOwner && gate.isDeptAdmin && gate.myDepartmentId) {
      const dept = budget.departments.find((d) => d.department_id === gate.myDepartmentId)
      return dept?.sub_pool_balance ?? 0
    }
    if (departmentId) {
      const dept = budget.departments.find((d) => d.department_id === departmentId)
      return dept?.sub_pool_balance ?? budget.pool_balance
    }
    return budget.pool_balance
  }, [budget, gate, departmentId])

  async function handleSubmit() {
    if (scopeItems.length === 0) {
      setError(t('assignTask.itemsRequired'))
      return
    }
    const assigneeNum = Number(assigneeId)
    if (!assigneeNum) {
      setError(t('assignTask.assigneeRequired'))
      return
    }
    const quotaNum = Math.floor(Number(quota))
    if (!Number.isFinite(quotaNum) || quotaNum <= 0) {
      setError(t('assignTask.quotaInvalid'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const items: ProductionAssignmentItem[] = scopeItems.map((it) => ({
        item_type: it.item_type,
        item_public_id: it.item_public_id,
      }))
      await createProductionAssignments(productionId, {
        department_id: departmentId || undefined,
        assignments: [{ assignee_id: assigneeNum, quota: quotaNum, items }],
      })
      onAssigned?.()
      onClose()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'assignTask.failed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ProtoModal
      open={open}
      onClose={onClose}
      title={t('assignTask.title')}
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose} disabled={submitting}>
            {t('assignTask.cancel')}
          </ProtoBtn>
          <ProtoBtn size="sm" disabled={submitting || scopeItems.length === 0} onClick={() => void handleSubmit()}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KanbanSquare className="h-4 w-4" />}
            {t('assignTask.confirm')}
          </ProtoBtn>
        </>
      }
    >
      {error ? <p className="mb-3 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}
      <div className="space-y-3">
        <div>
          <ProtoLabel>{t('assignTask.scopeItems')}</ProtoLabel>
          {scopeItems.length === 0 ? (
            <p className="mt-1.5 text-xs text-rose-300">{t('assignTask.itemsRequired')}</p>
          ) : (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {scopeItems.map((it) => (
                <ProtoBadge key={`${it.item_type}:${it.item_public_id}`} tone="indigo">
                  {it.label ?? `${it.item_type} · ${it.item_public_id}`}
                </ProtoBadge>
              ))}
            </div>
          )}
        </div>
        {gate.isOwner ? (
          <div>
            <ProtoLabel>{t('assignTask.department')}</ProtoLabel>
            <ProtoSelect className="mt-1.5" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
              <option value="">{t('assignTask.departmentPool')}</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </ProtoSelect>
            <p className="mt-1 text-[10px] text-zinc-500">{t('assignTask.departmentPoolHint')}</p>
          </div>
        ) : null}
        <div>
          <ProtoLabel required>{t('assignTask.assignee')}</ProtoLabel>
          <ProtoSelect className="mt-1.5" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
            <option value="">{t('assignTask.pickMember')}</option>
            {assigneeOptions.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </ProtoSelect>
        </div>
        <div>
          <ProtoLabel required>{t('assignTask.quota')}</ProtoLabel>
          <ProtoInput
            type="number"
            min={1}
            className="mt-1.5"
            value={quota}
            onChange={(e) => setQuota(e.target.value)}
          />
          {availableHint != null ? (
            <p className="mt-1 text-[10px] text-zinc-500">
              {t('assignTask.availableBalance', { n: availableHint })}
            </p>
          ) : null}
        </div>
      </div>
    </ProtoModal>
  )
}
